// FE-SCRM-052 — Opportunities list view types.
//
// Shapes the BE-SCRM-004 ``GET /api/v1/sales/opportunities`` payload
// after this chunk extended it with the filter surface the list
// view needs. The base ``OpportunityDetail`` fields stay shared with
// the detail page; list-specific fields (``prospect_name``,
// ``cadence_health``, ``last_touchpoint_at``) ride alongside.

import type {
  OpportunityStage,
  OpportunityType,
} from "@/features/sales/OpportunityDetail/types";

export type CadenceHealth =
  | "ahead"
  | "on_track"
  | "behind"
  | "critical"
  | "not_yet_urgent";


export type LivesBand =
  | "micro"
  | "small"
  | "mid_market"
  | "large"
  | "enterprise";


export type LostReason =
  | "incumbent_retained"
  | "lost_to_named_broker"
  | "price"
  | "no_decision"
  | "account_dissolved"
  | "account_acquired_by_parent"
  | "service_concern"
  | "relationship_concern"
  | "compliance_concern"
  | "coverage_gap_concern"
  | "other";


export type RenewalConfidence =
  | "confirmed"
  | "inferred_from_form5500"
  | "producer_estimate"
  | "unknown";


export interface OpportunityListRow {
  opportunity_id: string;
  prospect_id: string;
  prospect_name: string | null;
  name: string;
  opportunity_type: OpportunityType | string;
  stage: OpportunityStage | string;
  producer_id: string | null;
  owner_id: string | null;
  broker_location_id: string | null;
  estimated_lives: number | null;
  lives_band: LivesBand | null;
  estimated_annual_revenue_cents: number | null;
  estimated_commission_cents: number | null;
  probability: number | null;
  renewal_month: number | null;
  renewal_year: number | null;
  cadence_health: CadenceHealth | string | null;
  last_touchpoint_at: string | null;
  closed_won_at: string | null;
  closed_lost_at: string | null;
  lost_reason: LostReason | string | null;
  created_at: string | null;
  updated_at: string | null;
}


export interface OpportunityListResponse {
  rows: OpportunityListRow[];
  total: number;
  page: number;
  page_size: number;
}


// URL-encoded filter state. Mirrors the BE filter surface
// one-for-one so the FE never has to translate names.
export interface OpportunityListFilters {
  stage: string[];
  opportunity_type: string[];
  producer: string[];
  broker_location: string[];
  lives_band: LivesBand[];
  renewal_confidence: RenewalConfidence[];
  cadence_health: CadenceHealth[];
  lost_reason: LostReason[];
  alerts_only: boolean;
  closed_at_after: string | null;
  closed_at_before: string | null;
}


export const EMPTY_FILTERS: OpportunityListFilters = {
  stage: [],
  opportunity_type: [],
  producer: [],
  broker_location: [],
  lives_band: [],
  renewal_confidence: [],
  cadence_health: [],
  lost_reason: [],
  alerts_only: false,
  closed_at_after: null,
  closed_at_before: null,
};
